'use es6';

import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import I18n from 'I18n';
import PerformantDragLayer from 'layout-dnd-components/PerformantDragLayer';
import EPAddableTile from 'editor-components/sidebar/addableComponents/EPAddableTile';
import WidgetIcon from 'ContentComponents/components/cmv2/WidgetIcon';
import { getDragItemIsHidden } from 'InpageEditorUI/redux/selectors/layoutSectionDndSelectors';
import { isResizeHandleType } from 'InpageEditorUI/lib/layoutSection/resizeHandleHelpers';
import { DRAG_PREVIEW_HEIGHT_PX } from 'InpageEditorUI/lib/layoutSection/constants';
import { getAllModuleSchemas } from 'ContentEditorUI/redux/selectors/moduleSchemaSelectors';
import { getIsEditingGridLayouts, getModules } from 'ContentEditorUI/redux/selectors/moduleSelectors';
import { ColumnIcon } from 'foundations-assets/icons/components/ColumnIcon';
import { RowIcon } from 'foundations-assets/icons/components/RowIcon';
import { SectionIcon } from 'foundations-assets/icons/components/SectionIcon';
import { GridIcon } from 'foundations-assets/icons/components/GridIcon';
import DragPreview from 'layout-dnd-components/DragPreview';
import { getContainerElementById, getSectionElementById, getModuleElementById } from 'layout-dnd-utils/grids/render/domSelectors';

// "customModules" acts as the default if we cannot find the iconUrl
import { jsx as _jsx } from "react/jsx-runtime";
const BAYMAX_TILE_SIZE = 36;
const HiddenDragPreview = styled.div.withConfig({
  displayName: "LayoutSectionCustomDragLayer__HiddenDragPreview"
})([".hs-private.hs-private.hs-private &{opacity:0.9;position:absolute;left:-18px;top:-12px;z-index:-1;}"]);
const getLayoutSectionIcon = ({
  dragParentId,
  isColumn,
  isRow,
  isGrid,
  originLayoutSectionId
}) => {
  if (isColumn) {
    return ColumnIcon;
  } else if (isGrid) {
    return GridIcon;
  } else if (isRow && dragParentId === originLayoutSectionId) {
    return SectionIcon;
  }
  return RowIcon;
};
const getGridLayoutElement = ({
  dragId,
  dragParentId,
  isColumn,
  isRow,
  isGrid,
  originLayoutSectionId
}) => {
  if (isRow && dragParentId === originLayoutSectionId) {
    return getSectionElementById(dragId);
  } else if (isColumn || isGrid || isRow) {
    return getContainerElementById(dragId);
  }
  return getModuleElementById(dragId);
};
const getLayoutSectionLabel = ({
  isColumn,
  isRow,
  isGrid,
  dragParentId,
  originLayoutSectionId
}) => {
  if (isColumn) {
    return I18n.text('inpage.tile.column.label');
  }
  if (isGrid) {
    return I18n.text('inpage.tile.grid.label');
  }
  if (isRow && dragParentId === originLayoutSectionId) {
    return I18n.text('inpage.tile.section.label');
  }
  return I18n.text('inpage.tile.row.label');
};
class LayoutSectionCustomDragLayer extends Component {
  shouldRenderCustomDragIcon() {
    const {
      dragItemIsHidden,
      isDragging,
      item
    } = this.props;

    // If the item has a widgetCid, it is from the Inpage Sidebar Add Module panel.
    // In this scenario, we don't want to show the drag icon because those drags are using
    // the old-style Flex Column drag icons.
    // NOTE We will fix this soon to have those drags use the new-style Layout Section custom drag icons.
    return isDragging && item && item.originLayoutSectionId &&
    // Don't show drag icon for Flex Column module drags.
    !dragItemIsHidden && !item.widgetCid && !isResizeHandleType(item.type);
  }
  renderIcon(icon) {
    if (icon) {
      return /*#__PURE__*/_jsx(WidgetIcon, {
        size: BAYMAX_TILE_SIZE,
        color: "icon-core-subtle",
        icon: icon
      });
    }
    return null;
  }
  renderCustomDragIconForCleanLinesEditorUX() {
    const {
      item,
      modules,
      schemas,
      sourceClientOffset,
      differenceFromInitialOffset,
      isEditingGridLayouts
    } = this.props;
    const module = modules[item.dragId];
    const moduleId = module && module.get('module_id');
    const schema = moduleId && Object.values(schemas).find(_schema => _schema.moduleId === moduleId);
    const gridLayoutNodeElement = getGridLayoutElement(item);
    if (item && isEditingGridLayouts && gridLayoutNodeElement) {
      return /*#__PURE__*/_jsx(DragPreview, {
        element: gridLayoutNodeElement,
        sourceClientOffset: sourceClientOffset,
        differenceFromInitialOffset: differenceFromInitialOffset
      });
    }
    const LayoutSectionIcon = getLayoutSectionIcon(item);
    if (schema && schema.icon) {
      return /*#__PURE__*/_jsx(HiddenDragPreview, {
        children: /*#__PURE__*/_jsx(EPAddableTile, {
          useOnDark: true,
          iconNode: this.renderIcon(schema.icon),
          text: schema.displayName
        })
      });
    }
    return /*#__PURE__*/_jsx(HiddenDragPreview, {
      children: /*#__PURE__*/_jsx(EPAddableTile, {
        useOnDark: true,
        iconNode: /*#__PURE__*/_jsx(LayoutSectionIcon, {
          size: "850",
          color: "icon-core-subtle"
        }),
        text: schema && schema.displayName || getLayoutSectionLabel(item)
      })
    });
  }
  render() {
    if (this.shouldRenderCustomDragIcon()) {
      return this.renderCustomDragIconForCleanLinesEditorUX();
    }
    return null;
  }
}
LayoutSectionCustomDragLayer.propTypes = {
  dragItemIsHidden: PropTypes.bool.isRequired,
  item: PropTypes.object,
  modules: PropTypes.array,
  schemas: PropTypes.array,
  isDragging: PropTypes.bool
};
LayoutSectionCustomDragLayer.propTypes = {
  isDragging: PropTypes.bool,
  item: PropTypes.object,
  schemas: PropTypes.object.isRequired,
  modules: PropTypes.object.isRequired
};
const performantDragLayerOptions = {
  zIndex: 2,
  dragPreviewHeightPx: DRAG_PREVIEW_HEIGHT_PX,
  dragPreviewWidthPx: DRAG_PREVIEW_HEIGHT_PX
};
const collect = monitor => {
  return {
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    sourceClientOffset: monitor.getSourceClientOffset(),
    differenceFromInitialOffset: monitor.getDifferenceFromInitialOffset()
  };
};
const mapStateToProps = state => {
  return {
    dragItemIsHidden: getDragItemIsHidden(state),
    schemas: getAllModuleSchemas(state),
    modules: getModules(state),
    isEditingGridLayouts: getIsEditingGridLayouts(state)
  };
};
export default connect(mapStateToProps)(PerformantDragLayer(collect, performantDragLayerOptions)(LayoutSectionCustomDragLayer));